/**
 * Which route belongs to which CMS screen.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 *
 * Three lists used to describe "the pages of this CMS", and none of them agreed:
 *
 *   1. `GET /api/RolePage/GetRolePages` - the MASTER list of assignable pages.
 *      It is what the assign-pages modal draws, and it is flat:
 *      `{pageId, pageName, parentId, name, icon, route, orderIndex}`.
 *   2. `POST /api/Auth/login -> cmsPages` (and `GET /api/Auth/GetPageAccess`) -
 *      ONE USER's subset of the same pages, as the nested tree the sidebar
 *      renders: `{orderIndex, name, path, icon, nested}`.
 *   3. `routes/MainRoutes.jsx` - the ~75 routes that actually exist, with the
 *      permission check COMMENTED OUT, so any signed-in operator could open any
 *      screen by typing its URL.
 *
 * (1) and (2) are the same rows in two shapes, joined by the page's ROUTE.
 * Nothing joined either of them to (3), so assigning a page changed the menu
 * and nothing else.
 *
 * ---------------------------------------------------------------------------
 * THE MODEL
 *
 * A screen's identity is the first path segment under `/home` - `news`,
 * `faculty`, `homeslider`. That one key is derivable from a master row
 * (`route: "/home/news"`), from a sidebar row (`path: "/home/news"`) and from
 * the URL in the address bar, so the join needs no table at all for the LIST
 * screens.
 *
 * What the backend cannot know is that `/home/add-news` and `/home/edit-news/5`
 * are also "the news page". That is the only thing this file holds: the
 * sub-routes each screen owns. Granting the news page must carry its editor
 * with it, or the operator gets a list they cannot act on.
 *
 * ---------------------------------------------------------------------------
 * HOW TO ADD A SCREEN
 *
 * If the backend serves it as its own row and it has no add/edit forms, do
 * NOTHING - it gates itself. Otherwise add one entry to `SCREEN_SUBROUTES`:
 * the owning segment, and every other first segment that belongs to it.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS *NOT* ENFORCED, AND WHY
 *
 * A screen is enforced only while it exists in the master list - see
 * `isGateable` in `services/pageAccess.js`. Enforcing a screen the backend
 * never offers would hide it from EVERYBODY, administrators included, with no
 * way to grant it back. As of the master list read on 2026-10-21 these routes
 * exist but are not assignable, so they stay open:
 *
 *     home-settings     contact-settings   council      email / new-email
 *     applicants        leads              quizzes      academic-research
 *
 * Each one starts being enforced, with no code change, the day it appears in
 * `GetRolePages`. `logUngatedScreens()` prints the current list in development
 * so the gap stays visible rather than being rediscovered later.
 */

/**
 * Route segments every signed-in operator may open, whatever they hold.
 *
 * `profile` is the operator's own account page - gating it would mean a user
 * with no pages could not even see who they are signed in as.
 */
export const ALWAYS_OPEN_SEGMENTS = [
  '', // `/home` itself - it only ever redirects to the landing page
  'profile',
  'no-permission',
];

/**
 * owner segment -> the other first-segments that belong to that screen.
 *
 * Lower-case throughout; `segmentOfPath` lower-cases before it looks anything
 * up, because the backend serves `/home/HomeSlider` and `/home/happinessMeter`
 * while `MainRoutes` also registers `/home/home-slider`.
 *
 * The owner itself does not need to be repeated in its own list.
 */
export const SCREEN_SUBROUTES = {
  // The API serves this as `/home/HomeSlider`; `MainRoutes` registers both
  // spellings, and React Router matches case-insensitively, so both must map
  // to the same owner or the hyphenated URL would bypass the gate.
  homeslider: ['home-slider', 'add-home-slider', 'edit-home-slider'],

  announcements: ['add-announcement', 'edit-announcement'],

  news: ['add-news', 'edit-news'],

  events: [
    'add-events',
    'edit-event',
    'add-event-countdown',
    'edit-event-countdown',
  ],

  newsletters: ['add-newsletters', 'edit-newsletters'],

  media: ['media-gallery'],

  // The speech editor is reached from the testimonials screen, not the menu.
  testimonials: ['create-speech', 'update-speech'],

  faculty: ['add-faculty', 'edit-faculty'],

  alumni: [
    'add-student-alumni',
    'edit-student-alumni',
    'add-success-story',
    'edit-success-story',
    'add-ambassador',
    'edit-ambassador',
  ],

  council: [
    'add-council-member',
    'edit-council-member',
    'add-council-news',
    'edit-council-news',
    'create-council-calendar',
    'edit-council-calendar',
  ],

  'research-opportunities': [
    'create-research-opportunity',
    'edit-research-opportunity',
  ],

  'echo-magazine': ['echomagazine'],
  'guide-lines': ['guidelines'],

  email: ['new-email'],
};

/**
 * Arabic label -> owner segment, for the ONE case where a row has no route.
 *
 * The master list spells the same screen several ways and prefixes some names
 * with a literal `\r\n` (`"\r\nالفعاليات"`), so this is a fallback, never the
 * primary join - `route` is matched first and wins. `normaliseName` folds the
 * whitespace, the hamza forms and the taa marbuta before anything is compared.
 */
export const NAME_TO_SEGMENT = {
  'المستخدمون': 'users',
  'البرامج': 'programs',
  'الأخبار': 'news',
  'الفعاليات': 'events',
  'المنشورات': 'newsletters',
  'النشرة الإخبارية': 'newsletters',
  'معرض الصور': 'media',
  'الشهادات والتوصيات': 'testimonials',
  'الكلية': 'faculty',
  'الخريجون': 'alumni',
  'قصص نجاح الخريجين': 'alumni',
  'المجلس': 'council',
  'إعدادات': 'attributes',
  'الإعدادات': 'attributes',
  'الاحصائيات': 'statistics',
  'الإحصائيات': 'statistics',
  'التقويم الأكاديمي': 'academic-calendar',
  'محتوى الصفحة الرئيسية': 'homeslider',
  'إعدادات الصفحة الرئيسية': 'home-settings',
  'رسائل التواصل': 'contact-messages',
  'إعدادات جهات التواصل': 'contact-settings',
  'الإعلانات': 'announcements',
  'عدد الزوار': 'visitor-counter',
  'المنح الدراسية': 'scholarships',
  'المرافق': 'facilities',
  'مقياس السعادة': 'happinessmeter',
  // NOTE: the master list routes this one to `/home/research`, NOT to
  // `/home/academic-research`. Verified against the live list, 2026-10-21.
  'الأبحاث العلمية': 'research',
  'البحوث والدراسات': 'research',
  'النشرات الإخبارية': 'newslettersubscription',
  'الاشتراك في النشرة الإخبارية': 'newslettersubscription',
  'فرص البحث': 'research-opportunities',
  'مجلة الأمن والقانون': 'security-and-law',
  'الأمن والقانون': 'security-and-law',
  'مجلة صدى الأكاديمية': 'echo-magazine',
  'مجلة صدى الأكاديمي': 'echo-magazine',
  'إرشادات': 'guide-lines',
  'الإرشادات': 'guide-lines',
};

/**
 * THE FALLBACK MASTER LIST — a dated snapshot, used only when the live one
 * cannot be read.
 *
 * WHY THIS IS NEEDED. Enforcement is scoped to what `GET /api/RolePage/
 * GetRolePages` offers. Until now that endpoint was called from ONE screen —
 * Users — which only an administrator opens, so it is entirely possible that it
 * is administrator-only. If a restricted operator gets 401/403 from it, their
 * assignable set would be empty, "nothing is enforceable" would be the honest
 * answer, and the gate would fall open for precisely the people it exists to
 * restrict. That failure would be silent.
 *
 * So when neither the live list nor the cached one can be read, this stands in.
 * It is not a guess: it is the 25 screens the live list actually contained on
 * 2026-10-21, verified route by route with
 * `node scripts/check-page-access.mjs`. The eight screens the backend does NOT
 * offer are deliberately absent, so falling back cannot make a working screen
 * unreachable.
 *
 * WHEN TO UPDATE IT. Whenever the backend adds or removes an assignable page:
 * re-run the script against a fresh response and paste the enforced list here.
 * Being out of date is not dangerous — it only applies when the live list is
 * unavailable, and it errs towards the routes that were known to be grantable.
 */
export const DEFAULT_ASSIGNABLE_SEGMENTS = [
  'academic-calendar',
  'alumni',
  'announcements',
  'attributes',
  'contact-messages',
  'echo-magazine',
  'events',
  'facilities',
  'faculty',
  'guide-lines',
  'happinessmeter',
  'homeslider',
  'media',
  'news',
  'newsletters',
  'newslettersubscription',
  'programs',
  'research',
  'research-opportunities',
  'scholarships',
  'security-and-law',
  'statistics',
  'testimonials',
  'users',
  'visitor-counter',
];

/**
 * Screens that exist as routes but that the backend does not serve as
 * assignable pages. Listed only so the warning in `logUngatedScreens` can say
 * something useful; the enforcement rule reads the live master list, not this.
 */
export const KNOWN_ROUTE_SEGMENTS = [
  'users',
  'programs',
  'homeslider',
  'home-settings',
  'contact-messages',
  'contact-settings',
  'announcements',
  'visitor-counter',
  'academic-calendar',
  'attributes',
  'statistics',
  'email',
  'applicants',
  'leads',
  'scholarships',
  'facilities',
  'research',
  'quizzes',
  'happinessmeter',
  'newslettersubscription',
  'news',
  'events',
  'newsletters',
  'media',
  'echo-magazine',
  'guide-lines',
  'security-and-law',
  'academic-research',
  'research-opportunities',
  'council',
  'faculty',
  'alumni',
  'testimonials',
];
